// Empty string = same-origin (LB routes /api/* to the backend) so cookies stay first-party.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
// Least-privilege scope: per-file access to only the files WE create.
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";

/**
 * Request a short-lived Google Drive access token via the GIS token client.
 * Shows an incremental-consent popup the first time; the token lives only long
 * enough to complete the save and is never persisted.
 */
export function requestDriveAccessToken() {
  return new Promise((resolve, reject) => {
    const oauth2 = window.google?.accounts?.oauth2;
    if (!oauth2) {
      reject(new Error("Google services are still loading. Please try again."));
      return;
    }
    try {
      const client = oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: DRIVE_SCOPE,
        callback: (resp) => {
          if (resp && resp.access_token) resolve(resp.access_token);
          else reject(new Error(resp?.error_description || resp?.error || "Drive access was not granted."));
        },
        error_callback: (err) => reject(new Error(err?.message || "Drive authorization was cancelled.")),
      });
      client.requestAccessToken();
    } catch (e) {
      reject(e);
    }
  });
}

/**
 * Save the given report references (each { gcs_path, file_name, file_type }) to
 * the signed-in user's Google Drive.
 *
 * The backend streams progress as each file lands in Drive (Server-Sent Events,
 * same wire format as /api/chat/send), so `onProgress({ done, total })` fires
 * once per saved file — real progress, not a guess. Resolves with
 * { folder_link, files: [...] } once the backend emits "complete".
 */
export async function saveReportsToDrive(files, onProgress) {
  const access_token = await requestDriveAccessToken();

  const response = await fetch(`${BACKEND_URL}/api/chat/save-to-drive`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ access_token, files }),
  });

  if (!response.ok) {
    let detail = `HTTP ${response.status}`;
    try {
      detail = (await response.json())?.detail || detail;
    } catch (_) {
      /* body wasn't JSON — keep the generic message */
    }
    throw new Error(detail);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines[lines.length - 1];

    for (let i = 0; i < lines.length - 1; i++) {
      const line = lines[i].trim();
      if (!line || !line.startsWith("data: ")) continue;

      const event = JSON.parse(line.slice(6));
      if (event.type === "progress" || event.type === "start") {
        onProgress?.({ done: event.done || 0, total: event.total || 0 });
      } else if (event.type === "error") {
        throw new Error(event.content || "Could not save to Google Drive.");
      } else if (event.type === "complete") {
        return { folder_link: event.folder_link, files: event.files };
      }
    }
  }

  throw new Error("Connection to server was lost before the save finished.");
}
